import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { ModalSize, useModal } from 'components/modals/ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import LocationForm from '../../views/contacts/LocationForm';
import AssetBulkForm from './AssetBulkForm';

const AssetForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
    searchValue?: string;
    modalSize?: ModalSize;
    title?: string;
}> = ({
    viewKey,
    linkedItem,
    searchValue,
    modalSize,
    title,
}) => {
    const { t } = useTranslation();
    const { updateModalProps } = useModal();
    const apiObject = 'asset';

    // Switch between adding one object and adding several objects at once
    const [bulkMode, setBulkMode] = useState(false);

    // Remember the modal size and title of the single form to restore them
    const singleModalProps = useRef<{ modalSize?: ModalSize; title?: string }>({ modalSize, title });

    // Hide the location field when the location is already given by the linked item
    const hasLinkedLocation = Boolean(linkedItem?.location);

    // Widen the modal and switch to the bulk form
    const switchToBulkMode = () => {
        if (viewKey) {
            updateModalProps(viewKey, { modalSize: 'medium', title: t('asset.general.bulk_title') });
        }
        setBulkMode(true);
    };

    // Restore the modal and switch back to the single form
    const switchToSingleMode = () => {
        if (viewKey) {
            updateModalProps(viewKey, {
                modalSize: singleModalProps.current.modalSize ?? 'add-form-smaller',
                title: singleModalProps.current.title,
            });
        }
        setBulkMode(false);
    };

    const fields: FieldType[] = [
        ...(!hasLinkedLocation ? [{
            type: 'searchselect',
            name: 'location',
            label: 'asset.general.location_long_label',
            placeholder: 'asset.general.location_placeholder',
            objectName: 'location.general.object_name.singular',
            query: { endpoint: 'get_location_search' },
            selectionFormat: 'address',
            optionFormat: { title: { field: 'address', format: (value) => value }},
            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
        } as FieldType] : []),
        {
            name: 'name',
            label: 'asset.general.name_label',
            type: 'text',
            placeholder: 'asset.general.name_placeholder',
            ...(searchValue ? { defaultValue: searchValue } : {}),
        },
        {
            type: 'dropdown',
            name: 'asset_type',
            label: 'asset.general.asset_type_label',
            apiRequest: [{
                endpoint: 'get_asset_type_list',
                object: 'asset_type',
                params: { deleted: false },
                useCache: true
            }],
            disabled_selected: 'asset.general.type_disabled_selected',
            allowNoneOption: false,
        },
        {
            name: 'number',
            label: 'asset.general.number_label',
            type: 'text',
            placeholder: 'asset.general.number_placeholder',
        },
        {
            name: 'description',
            label: 'asset.general.description_label',
            type: 'textarea',
            rows: 4,
            placeholder: 'asset.general.description_placeholder',
        },
        {
            type: 'header',
        },
        {
            type: 'searchselect',
            name: 'parent_asset',
            label: 'asset.general.parent_asset_label',
            placeholder: 'asset.general.parent_asset_placeholder',
            tooltipText: 'asset.general.parent_asset_tooltip',
            objectName: 'asset.general.object_name.singular',
            query: (updatedData) => {
                const locationId = updatedData.location ?? linkedItem?.location;
                if (locationId) {
                    return {
                        endpoint: `get_asset_search/${locationId}`
                    };
                }
                return {
                    endpoint: 'get_asset_search'
                };
            },
            showOptionsOnClick: (updatedData) => {
                const locationId = updatedData.location ?? linkedItem?.location;
                if (!locationId) return null;
                return {
                    endpoint: `get_asset_search/${locationId}`
                };
            },
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'number', format: (value) => value, optional: true },
            },
            directOptionsTitle: 'asset.general.assets_of_location_label',
        },
    ];

    // Render the bulk form which adds several objects in one go
    if (bulkMode) {
        return (
            <AssetBulkForm
                viewKey={viewKey}
                linkedItem={linkedItem}
                searchValue={searchValue}
                onSwitchToSingle={switchToSingleMode}
            />
        );
    }

    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
            extraLeftButtons={
                <SecondaryButton
                    onClick={switchToBulkMode}
                    label='asset.general.add_multiple_button_label'
                    size='small'
                    onlyViewRestriction={true}
                />
            }
        />
    );
};

export default AssetForm;
